import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { isNative } from './device.js';

/**
 * Crash and error reporting (Firebase Crashlytics, native builds only).
 *
 * What goes in: our own error messages and codes, the place they came from,
 * and the JS stack. What never goes in: angles, scores, landmarks or anything
 * else from a session — the same line as GA4 (CLAUDE.md "하지 말 것"). The web
 * build logs to the console instead.
 */
export async function reportError(where: string, error: unknown, keys: Record<string, string> = {}): Promise<void> {
  const message = error instanceof Error ? error.message : String(error);
  if (!isNative()) {
    console.error(`[${where}]`, error);
    return;
  }
  try {
    const stack = error instanceof Error ? error.stack : undefined;
    if (stack) await FirebaseCrashlytics.log({ message: stack.slice(0, 2000) });
    await FirebaseCrashlytics.recordException({
      message: `${where}: ${message}`.slice(0, 500),
      keysAndValues: Object.entries({ where, ...keys }).map(([key, value]) => ({ key, value, type: 'string' as const })),
    });
  } catch {
    // Reporting must never become the error.
  }
}

/** A breadcrumb that rides along with the next report. */
export function breadcrumb(message: string): void {
  if (!isNative()) return;
  void FirebaseCrashlytics.log({ message }).catch(() => undefined);
}

/** The Firebase uid, so one person's reports group together. Null on sign-out. */
export function setCrashUser(uid: string | null): void {
  if (!isNative()) return;
  void FirebaseCrashlytics.setUserId({ userId: uid ?? '' }).catch(() => undefined);
}

/** Uncaught errors and rejected promises anywhere in the app. */
export function installErrorReporting(): void {
  window.addEventListener('error', (event) => {
    void reportError('uncaught', event.error ?? event.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    void reportError('unhandled-rejection', event.reason);
  });
}
